import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { toast } from 'react-hot-toast';
import communityService, {
  MabarEvent,
  GameListItem,
  StandingItem,
  MabarParticipant,
} from '@/services/communityService';
import mabarService from '@/services/mabarService';
import invasionService, { InvasionSession } from '@/services/invasionService';
import { Skeleton } from '@/components/shadcn/skeleton';
import { Button as ShadcnButton } from '@/components/shadcn/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/shadcn/sheet';
import GenerateGameOtomatisModal from '@/components/community/GenerateGameOtomatisModal';
import StandingCard from '@/components/community/StandingCard';
import InvasionPlayerLeaderboard, {
  InvasionPlayerLeaderboardItem,
} from '@/components/community/InvasionPlayerLeaderboard';
import { downloadOrShareBlob } from '@/utils/downloadBlob';
import { LeaderboardPlayerItem } from '@/components/ui/recap/LeaderboardRecapCard';
import { formatLocalEventTime, formatLocalEventTimeRange } from '@/utils/dateUtils';

const formatEventDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

type GameStatus = 'SELESAI' | 'LIVE MATCH' | 'UPCOMING';

const getEventStatus = (event: MabarEvent): GameStatus => {
  const now = new Date();
  const startTime = event.start_time ? new Date(event.start_time) : null;
  const endTime = event.end_time ? new Date(event.end_time) : null;

  if (endTime && now >= endTime) return 'SELESAI';
  if (startTime && now >= startTime && (!endTime || now < endTime)) return 'LIVE MATCH';
  return 'UPCOMING';
};

const getStatusLabel = (status: GameStatus): string => {
  switch (status) {
    case 'SELESAI':
      return 'Selesai';
    case 'LIVE MATCH':
      return 'Live Match';
    default:
      return 'Upcoming';
  }
};

type ActiveTab = 'match-result' | 'standings' | 'top-score' | 'top-assist' | 'top-mmr' | 'top-save';

const MabarAnalitikPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<MabarEvent | null>(null);
  const [games, setGames] = useState<GameListItem[]>([]);
  const [standing, setStanding] = useState<StandingItem[]>([]);
  const [invasionSession, setInvasionSession] = useState<InvasionSession | null>(null);
  const [participants, setParticipants] = useState<MabarParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [isGenerateBulkModalOpen, setIsGenerateBulkModalOpen] = useState(false);
  const [gameToDelete, setGameToDelete] = useState<{ slug: string; label: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [downloadingStatistik, setDownloadingStatistik] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as ActiveTab | null;
  const validTabs: ActiveTab[] = ['match-result', 'standings', 'top-score', 'top-assist', 'top-mmr', 'top-save'];
  const initialTab = tabParam && validTabs.includes(tabParam) ? tabParam : 'match-result';
  const [tab, setTab] = useState<ActiveTab>(initialTab);

  useEffect(() => {
    if (tabParam && validTabs.includes(tabParam) && tabParam !== tab) {
      setTab(tabParam);
    }
  }, [tabParam, tab]);

  const handleTabChange = (newTab: ActiveTab) => {
    setTab(newTab);
    setSearchParams({ tab: newTab }, { replace: true });
  };

  const loadData = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const [eventData, gamesData, standingData, sessionData, participantsRes] = await Promise.all([
        communityService.getEventBySlug(slug),
        communityService.getGamesByEventSlug(slug),
        communityService.getStandingsByEventSlug(slug),
        invasionService.getInvasionSession(slug).catch(() => null),
        communityService.getEventParticipants(slug, { limit: 100 }).catch(() => null),
      ]);
      setEvent(eventData);
      setGames(gamesData ?? []);
      setStanding(standingData ?? []);
      setInvasionSession(sessionData);
      setParticipants(participantsRes?.participants ?? []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Gagal memuat data';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Aggregate stats across games and invasion session
  const playerStats = useMemo<InvasionPlayerLeaderboardItem[]>(() => {
    const map: Record<string, InvasionPlayerLeaderboardItem> = {};

    // 1. Initialize from participants list
    participants.forEach((p) => {
      const key = String(p.id);
      map[key] = {
        id: p.id,
        name: p.name,
        avatar: p.avatar,
        role: p.role,
        team: p.team,
        goals: 0,
        assists: 0,
        saves: 0,
        deltaMmr: 0,
        matchesPlayed: 0,
      };
    });

    const participantIdToKey = (pid?: number | null) => {
      if (!pid) return null;
      return String(pid);
    };

    const userIdToKey = (uid?: number | null) => {
      if (!uid) return null;
      const found = participants.find((p) => p.user_id === uid);
      return found ? String(found.id) : null;
    };

    // 2. Count goals, assists, and saves from timelines of all games
    const timelineGoals: Record<string, number> = {};
    const timelineAssists: Record<string, number> = {};
    const timelineSaves: Record<string, number> = {};

    games.forEach((g) => {
      if (!Array.isArray(g.timeline)) return;
      g.timeline.forEach((item: unknown) => {
        if (!item || typeof item !== 'object') return;
        const e = item as { type?: string; player_id?: number; assist_id?: number };
        if (e.type === 'goal') {
          if (e.player_id) {
            const k = String(e.player_id);
            timelineGoals[k] = (timelineGoals[k] || 0) + 1;
          }
          if (e.assist_id) {
            const ak = String(e.assist_id);
            timelineAssists[ak] = (timelineAssists[ak] || 0) + 1;
          }
        } else if (e.type === 'save') {
          if (e.player_id) {
            const sk = String(e.player_id);
            timelineSaves[sk] = (timelineSaves[sk] || 0) + 1;
          }
        }
      });
    });

    // 3. Process matches & player stats from invasion session
    if (invasionSession?.matches && invasionSession.matches.length > 0) {
      invasionSession.matches.forEach((match) => {
        const isCompleted = match.status === 'completed';
        (match.players || []).forEach((mp) => {
          let key = participantIdToKey(mp.participantId) || userIdToKey(mp.userId);
          if (!key) {
            key = `mp_${mp.id}`;
            map[key] = {
              id: mp.id,
              name: mp.user?.name || mp.user?.nickname || 'Pemain',
              avatar: (mp.user as { avatar_url?: string; avatar?: string } | undefined)?.avatar_url || (mp.user as { avatar_url?: string; avatar?: string } | undefined)?.avatar,
              role: mp.role,
              team: mp.team === 'A' ? match.teamAName : match.teamBName,
              goals: 0,
              assists: 0,
              saves: 0,
              deltaMmr: 0,
              matchesPlayed: 0,
            };
          }

          const p = map[key];
          if (p) {
            if (mp.stats) {
              p.goals += Number(mp.stats.goals) || 0;
              p.assists += Number(mp.stats.assists) || 0;
              p.saves = (p.saves || 0) + (Number((mp.stats as any).saves) || Number((mp.stats as any).save) || 0);
              p.deltaMmr += Number(mp.stats.deltaMmr) || 0;
            }
            if (isCompleted || mp.isOnField) {
              p.matchesPlayed += 1;
            }
            if (!p.role && mp.role) p.role = mp.role;
            if (!p.team && mp.team) {
              p.team = mp.team === 'A' ? match.teamAName : match.teamBName;
            }
            if (!p.avatar && ((mp.user as { avatar_url?: string; avatar?: string } | undefined)?.avatar_url || (mp.user as { avatar_url?: string; avatar?: string } | undefined)?.avatar)) {
              p.avatar = (mp.user as { avatar_url?: string; avatar?: string } | undefined)?.avatar_url || (mp.user as { avatar_url?: string; avatar?: string } | undefined)?.avatar;
            }
          }
        });
      });
    }

    // 4. Merge timeline goals/assists/saves using Math.max to prevent discrepancies
    Object.keys(timelineGoals).forEach((k) => {
      if (map[k]) {
        map[k].goals = Math.max(map[k].goals, timelineGoals[k]);
        if (map[k].matchesPlayed === 0 && map[k].goals > 0) {
          map[k].matchesPlayed = 1;
        }
      } else {
        map[k] = {
          id: k,
          name: `Pemain #${k}`,
          goals: timelineGoals[k],
          assists: timelineAssists[k] || 0,
          saves: timelineSaves[k] || 0,
          deltaMmr: 0,
          matchesPlayed: 1,
        };
      }
    });

    Object.keys(timelineAssists).forEach((k) => {
      if (map[k]) {
        map[k].assists = Math.max(map[k].assists, timelineAssists[k]);
      }
    });

    Object.keys(timelineSaves).forEach((k) => {
      if (map[k]) {
        map[k].saves = Math.max(map[k].saves || 0, timelineSaves[k]);
        if (map[k].matchesPlayed === 0 && (map[k].saves || 0) > 0) {
          map[k].matchesPlayed = 1;
        }
      } else {
        map[k] = {
          id: k,
          name: `Pemain #${k}`,
          goals: timelineGoals[k] || 0,
          assists: timelineAssists[k] || 0,
          saves: timelineSaves[k],
          deltaMmr: 0,
          matchesPlayed: 1,
        };
      }
    });

    return Object.values(map);
  }, [participants, games, invasionSession]);

  const topScorers = useMemo(() => {
    return [...playerStats]
      .filter((p) => p.goals > 0)
      .sort((a, b) => {
        if (b.goals !== a.goals) return b.goals - a.goals;
        if (b.assists !== a.assists) return b.assists - a.assists;
        return a.name.localeCompare(b.name);
      });
  }, [playerStats]);

  const topAssists = useMemo(() => {
    return [...playerStats]
      .filter((p) => p.assists > 0)
      .sort((a, b) => {
        if (b.assists !== a.assists) return b.assists - a.assists;
        if (b.goals !== a.goals) return b.goals - a.goals;
        return a.name.localeCompare(b.name);
      });
  }, [playerStats]);

  const topMmrGainers = useMemo(() => {
    return [...playerStats]
      .filter((p) => p.deltaMmr !== 0 || p.matchesPlayed > 0)
      .sort((a, b) => {
        if (b.deltaMmr !== a.deltaMmr) return b.deltaMmr - a.deltaMmr;
        if (b.goals !== a.goals) return b.goals - a.goals;
        return a.name.localeCompare(b.name);
      });
  }, [playerStats]);

  const topSavers = useMemo(() => {
    return [...playerStats]
      .filter((p) => (p.saves ?? 0) > 0)
      .sort((a, b) => {
        if ((b.saves ?? 0) !== (a.saves ?? 0)) return (b.saves ?? 0) - (a.saves ?? 0);
        if (b.goals !== a.goals) return b.goals - a.goals;
        return a.name.localeCompare(b.name);
      });
  }, [playerStats]);

  const handleShareLeaderboardLink = async () => {
    if (!slug) return;
    const shareUrl = `${window.location.origin}/event/${slug}/statistik?tab=${tab}`;
    const tabName =
      tab === 'top-score'
        ? 'Top Score'
        : tab === 'top-assist'
        ? 'Top Assist'
        : tab === 'top-save'
        ? 'Top Save'
        : 'Top MMR';
    const title = `${tabName} - ${event?.name || 'Mabar Event'}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: `Lihat klasemen ${tabName} pada event ${event?.name || ''}`,
          url: shareUrl,
        });
        return;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link berhasil disalin ke clipboard!');
    } catch {
      toast.error('Gagal menyalin link');
    }
  };

  const handleDownloadLeaderboardStatistik = async () => {
    if (!slug || !event) return;
    setDownloadingStatistik(true);
    try {
      const activePlayers =
        tab === 'top-score'
          ? topScorers
          : tab === 'top-assist'
          ? topAssists
          : tab === 'top-save'
          ? topSavers
          : topMmrGainers;

      if (activePlayers.length === 0) {
        toast.error('Belum ada data statistik untuk diunduh');
        return;
      }

      const leaderboardItems: LeaderboardPlayerItem[] = activePlayers.slice(0, 5).map((p, idx) => ({
        position: idx + 1,
        playerName: p.name,
        avatarUrl: p.avatar,
        team: p.team,
        role: p.role,
        matches: p.matchesPlayed,
        value:
          tab === 'top-score'
            ? p.goals
            : tab === 'top-assist'
            ? p.assists
            : tab === 'top-save'
            ? p.saves ?? 0
            : p.deltaMmr,
        goals: p.goals,
        assists: p.assists,
        deltaMmr: p.deltaMmr,
      }));

      const variantTemplate = `leaderboard_${tab.replace('-', '_')}`;
      const firstPlayer = leaderboardItems[0];
      const eventDateFormatted = formatEventDate(event.event_date);
      const location = event.lokasi_arena || event.address || '';
      const sportCategory = event.sport_category?.name || 'Mini Soccer';

      const blob = await mabarService.renderShareRecapV2(slug, {
        eventName: event.name,
        eventDateFormatted,
        stats: {
          goals: firstPlayer?.goals || 0,
          assists: firstPlayer?.assists || 0,
          saves: firstPlayer?.saves || 0,
          matches: firstPlayer?.matches || 0,
        },
        playerName: firstPlayer?.playerName || 'Pemain',
        playerAvatarUrl: firstPlayer?.avatarUrl,
        template: variantTemplate,
        sportCategory,
        location,
        standingsJson: JSON.stringify(leaderboardItems),
      });

      const filename = `statistik-${tab}-${slug}.png`;
      const result = await downloadOrShareBlob(blob, filename, { title: event.name });
      if (result === 'saved') {
        toast.success('Gambar statistik berhasil diunduh/disimpan ke Galeri');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Gagal mengunduh gambar statistik';
      toast.error(message);
    } finally {
      setDownloadingStatistik(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!gameToDelete || deleting) return;
    setDeleting(true);
    try {
      await communityService.deleteGame(gameToDelete.slug);
      setGameToDelete(null);
      loadData();
      toast.success('Game berhasil dihapus');
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      toast.error(message || (err instanceof Error ? err.message : 'Gagal menghapus game'));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto px-4 py-6">
        <Skeleton className="h-5 w-32 mb-4" />
        <Skeleton className="h-32 w-full rounded-lg mb-6" />
        <Skeleton className="h-6 w-48 mb-4" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="mx-auto px-4 py-6">
        <div className="text-center py-12">
          <Icon icon="mdi:alert-circle" className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">Event tidak ditemukan</p>
          <ShadcnButton className="mt-4" onClick={() => navigate('/host/mabar')}>
            Kembali ke Mabar
          </ShadcnButton>
        </div>
      </div>
    );
  }

  const venue = event.lokasi_arena || event.address;
  const sportName = event.sport_category?.name ?? '';
  const hostName = event.host?.name ?? '';
  const sportSlug = (event.sport_category?.slug ?? '').toLowerCase();
  const isInvasion =
    !['badminton', 'padel', 'pickleball', 'tennis'].includes(sportSlug) ||
    ['mini-soccer', 'minisoccer', 'futsal', 'sepak-bola', 'sepakbola', 'football', 'invasion'].includes(
      sportSlug
    );

  const getTeamBadgeStyle = (name?: string, fallbackIndex: number = 0) => {
    const n = (name || '').trim();
    const lower = n.toLowerCase();

    const parts = n.split(/\s+/);
    let initial = 'T';
    if (parts.length >= 2 && parts[0][0] && parts[1][0]) {
      initial = (parts[0][0] + parts[1][0]).toUpperCase();
    } else if (n.length >= 2) {
      initial = n.substring(0, 2).toUpperCase();
    } else if (n.length === 1) {
      initial = n.toUpperCase();
    }

    if (lower.includes('green') || lower.includes('hijau')) {
      return { bg: 'bg-[#E9F6EE]', text: 'text-[#22C55E]', border: 'border-[#BBF7D0]', initial };
    }
    if (lower.includes('navy') || lower.includes('biru')) {
      return { bg: 'bg-[#F0F4FA]', text: 'text-[#1E3A8A]', border: 'border-[#BFDBFE]', initial };
    }
    if (lower.includes('orange') || lower.includes('jingga')) {
      return { bg: 'bg-[#FFF5EE]', text: 'text-[#FC480D]', border: 'border-[#FFEDD5]', initial };
    }
    if (lower.includes('red') || lower.includes('merah')) {
      return { bg: 'bg-[#FEF2F2]', text: 'text-[#EF4444]', border: 'border-[#FCA5A5]', initial };
    }
    if (lower.includes('yellow') || lower.includes('kuning')) {
      return { bg: 'bg-[#FEFCE8]', text: 'text-[#EAB308]', border: 'border-[#FDE047]', initial };
    }

    const palettes = [
      { bg: 'bg-[#E9F6EE]', text: 'text-[#22C55E]', border: 'border-[#BBF7D0]' },
      { bg: 'bg-[#F0F4FA]', text: 'text-[#1E3A8A]', border: 'border-[#BFDBFE]' },
      { bg: 'bg-[#FFF5EE]', text: 'text-[#FC480D]', border: 'border-[#FFEDD5]' },
      { bg: 'bg-[#F5F3FF]', text: 'text-[#8B5CF6]', border: 'border-[#DDD6FE]' },
    ];
    return { ...palettes[fallbackIndex % palettes.length], initial };
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-4 sm:py-6 pb-24 relative">
      {/* Header Card (Event Info) */}
      <div className="bg-white rounded-2xl border border-[#E0E6ED] p-4 sm:p-5 shadow-2xs mb-5">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-[#FC480D] bg-[#FFF5EE]">
            <Icon icon="ph:soccer-ball-bold" className="w-3.5 h-3.5" />
            {sportName || 'Mini Soccer'}
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-[#3B7DDB] bg-[#EFF6FF] border border-[#BFDBFE]">
            <Icon icon="ph:clock-bold" className="w-3.5 h-3.5" />
            {getStatusLabel(getEventStatus(event)).toUpperCase()}
          </span>
        </div>

        <h2 className="text-base sm:text-xl font-bold text-[#0E1726] mb-4 leading-snug">
          {event.name}
        </h2>

        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <Icon icon="ph:calendar-dots-bold" className="w-4 h-4 text-[#FC480D] shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-xs text-gray-400 font-medium">Tanggal & Waktu</p>
              <p className="text-xs sm:text-sm font-semibold text-[#0E1726]">
                {formatEventDate(event.event_date)} · {formatLocalEventTimeRange(event.start_time, event.end_time)}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Icon icon="ph:user-bold" className="w-4 h-4 text-[#FC480D] shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-xs text-gray-400 font-medium">Host / Komunitas</p>
              <p className="text-xs sm:text-sm font-semibold text-[#0E1726]">{hostName || '-'}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Icon icon="ph:map-pin-bold" className="w-4 h-4 text-[#FC480D] shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-xs text-gray-400 font-medium">Venue</p>
              <p className="text-xs sm:text-sm font-semibold text-[#0E1726]">{venue || '-'}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Icon icon="ph:soccer-ball-bold" className="w-4 h-4 text-[#FC480D] shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-xs text-gray-400 font-medium">Jenis Olahraga</p>
              <p className="text-xs sm:text-sm font-semibold text-[#0E1726]">{sportName || '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Horizontal Pill Tabs Navigation */}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1 mb-5">
        <button
          type="button"
          onClick={() => handleTabChange('match-result')}
          className={`px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 shrink-0 whitespace-nowrap transition-all cursor-pointer border ${
            tab === 'match-result'
              ? 'bg-white text-[#FC480D] border-[#FC480D] shadow-2xs'
              : 'bg-white text-[#556575] border-[#E0E6ED] hover:bg-gray-50 hover:text-[#0E1726]'
          }`}
        >
          <Icon icon="ph:soccer-ball-bold" className={`w-4 h-4 shrink-0 ${tab === 'match-result' ? 'text-[#FC480D]' : 'text-gray-400'}`} />
          <span>Pertandingan</span>
        </button>

        <button
          type="button"
          onClick={() => handleTabChange('standings')}
          className={`px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 shrink-0 whitespace-nowrap transition-all cursor-pointer border ${
            tab === 'standings'
              ? 'bg-white text-[#FC480D] border-[#FC480D] shadow-2xs'
              : 'bg-white text-[#556575] border-[#E0E6ED] hover:bg-gray-50 hover:text-[#0E1726]'
          }`}
        >
          <Icon icon="ph:list-numbers-bold" className={`w-4 h-4 shrink-0 ${tab === 'standings' ? 'text-[#FC480D]' : 'text-gray-400'}`} />
          <span>Klasemen</span>
        </button>

        {isInvasion && (
          <>
            <button
              type="button"
              onClick={() => handleTabChange('top-score')}
              className={`px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 shrink-0 whitespace-nowrap transition-all cursor-pointer border ${
                tab === 'top-score'
                  ? 'bg-white text-[#FC480D] border-[#FC480D] shadow-2xs'
                  : 'bg-white text-[#556575] border-[#E0E6ED] hover:bg-gray-50 hover:text-[#0E1726]'
              }`}
            >
              <Icon icon="ph:target-bold" className={`w-4 h-4 shrink-0 ${tab === 'top-score' ? 'text-[#FC480D]' : 'text-gray-400'}`} />
              <span>Top Scores</span>
            </button>

            <button
              type="button"
              onClick={() => handleTabChange('top-assist')}
              className={`px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 shrink-0 whitespace-nowrap transition-all cursor-pointer border ${
                tab === 'top-assist'
                  ? 'bg-white text-[#FC480D] border-[#FC480D] shadow-2xs'
                  : 'bg-white text-[#556575] border-[#E0E6ED] hover:bg-gray-50 hover:text-[#0E1726]'
              }`}
            >
              <Icon icon="ph:handshake-bold" className={`w-4 h-4 shrink-0 ${tab === 'top-assist' ? 'text-[#FC480D]' : 'text-gray-400'}`} />
              <span>Top Assist</span>
            </button>

            <button
              type="button"
              onClick={() => handleTabChange('top-mmr')}
              className={`px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 shrink-0 whitespace-nowrap transition-all cursor-pointer border ${
                tab === 'top-mmr'
                  ? 'bg-white text-[#FC480D] border-[#FC480D] shadow-2xs'
                  : 'bg-white text-[#556575] border-[#E0E6ED] hover:bg-gray-50 hover:text-[#0E1726]'
              }`}
            >
              <Icon icon="ph:trend-up-bold" className={`w-4 h-4 shrink-0 ${tab === 'top-mmr' ? 'text-[#FC480D]' : 'text-gray-400'}`} />
              <span>Top MMR</span>
            </button>

            <button
              type="button"
              onClick={() => handleTabChange('top-save')}
              className={`px-4 py-2 rounded-full text-xs font-bold flex items-center gap-1.5 shrink-0 whitespace-nowrap transition-all cursor-pointer border ${
                tab === 'top-save'
                  ? 'bg-white text-[#FC480D] border-[#FC480D] shadow-2xs'
                  : 'bg-white text-[#556575] border-[#E0E6ED] hover:bg-gray-50 hover:text-[#0E1726]'
              }`}
            >
              <Icon icon="ph:hand-palm-bold" className={`w-4 h-4 shrink-0 ${tab === 'top-save' ? 'text-[#FC480D]' : 'text-gray-400'}`} />
              <span>Top Save</span>
            </button>
          </>
        )}
      </div>

      {/* Tab Content */}
      {tab === 'match-result' ? (
        games.length === 0 ? (
          <div className="bg-white rounded-2xl border border-[#E0E6ED] p-8 text-center shadow-2xs">
            <div className="w-16 h-16 rounded-2xl bg-[#FFF5EE] text-[#FC480D] flex items-center justify-center mx-auto mb-3">
              <Icon icon="ph:trophy-bold" className="w-8 h-8" />
            </div>
            <h3 className="text-base font-bold text-[#0E1726] mb-1">Belum Ada Pertandingan</h3>
            <p className="text-xs text-gray-500 max-w-xs mx-auto mb-4">
              Kelola pertandingan mabar Anda. Buat satu per satu sesuai kebutuhan.
            </p>
            <ShadcnButton
              onClick={() => navigate(`/host/mabar/${slug}/game/create`)}
              className="bg-[#FC480D] hover:bg-[#E8490F] text-white font-bold text-xs rounded-full px-5 h-10 inline-flex items-center gap-2 cursor-pointer"
            >
              <Icon icon="ph:plus-bold" className="w-4 h-4" />
              Tambah Game
            </ShadcnButton>
          </div>
        ) : (
          <div className="space-y-3">
            {games.map((game, index) => {
              const homeScore = Number(game.home_score) || 0;
              const awayScore = Number(game.away_score) || 0;
              const homeBadge = getTeamBadgeStyle(game.home_team_name, 0);
              const awayBadge = getTeamBadgeStyle(game.away_team_name, 1);

              let resultText = 'Seri';
              if (homeScore > awayScore) {
                resultText = `${game.home_team_name || 'Tim A'} Menang`;
              } else if (awayScore > homeScore) {
                resultText = `${game.away_team_name || 'Tim B'} Menang`;
              }

              return (
                <div
                  key={game.id}
                  className="bg-white rounded-2xl border border-[#E0E6ED] shadow-2xs overflow-hidden"
                >
                  {/* Game Card Header */}
                  <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[#F0F2F4]">
                    <div className="flex items-center gap-2">
                      <span className="font-extrabold text-xs text-[#0E1726]">GAME {index + 1}</span>
                      {game.start_time && (
                        <>
                          <span className="text-gray-300">|</span>
                          <span className="text-xs text-gray-500 font-medium">
                            {formatLocalEventTime(game.start_time, false)}
                            {game.end_time ? ` - ${formatLocalEventTime(game.end_time, false)}` : ''}
                          </span>
                        </>
                      )}
                    </div>
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#E9F6EE] text-[#22C55E] border border-[#BBF7D0]">
                      <Icon icon="ph:check-circle-fill" className="w-3.5 h-3.5" />
                      Selesai
                    </span>
                  </div>

                  {/* Game Matchup Body */}
                  <div className="p-4 flex items-center justify-between gap-2">
                    {/* Home Team */}
                    <div className="flex-1 min-w-0 flex flex-col items-center gap-2">
                      <div
                        className={`w-12 h-12 rounded-2xl ${homeBadge.bg} ${homeBadge.text} font-extrabold text-base flex items-center justify-center shadow-2xs border ${homeBadge.border}`}
                      >
                        {homeBadge.initial}
                      </div>
                      <span className="text-xs font-bold text-[#0E1726] text-center truncate max-w-[110px]">
                        {game.home_team_name || 'Home Team'}
                      </span>
                    </div>

                    {/* Score Center */}
                    <div className="flex-none flex flex-col items-center gap-1 px-2">
                      <span className="text-2xl sm:text-3xl font-extrabold text-[#0E1726] tracking-tight whitespace-nowrap">
                        {homeScore} - {awayScore}
                      </span>
                      <span className="text-[11px] font-medium text-gray-400">{resultText}</span>
                    </div>

                    {/* Away Team */}
                    <div className="flex-1 min-w-0 flex flex-col items-center gap-2">
                      <div
                        className={`w-12 h-12 rounded-2xl ${awayBadge.bg} ${awayBadge.text} font-extrabold text-base flex items-center justify-center shadow-2xs border ${awayBadge.border}`}
                      >
                        {awayBadge.initial}
                      </div>
                      <span className="text-xs font-bold text-[#0E1726] text-center truncate max-w-[110px]">
                        {game.away_team_name || 'Away Team'}
                      </span>
                    </div>
                  </div>

                  {/* Game Actions Footer */}
                  <div className="px-3 py-2.5 border-t border-[#F0F2F4] flex items-center gap-1.5 sm:gap-2 flex-nowrap overflow-x-auto no-scrollbar">
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/host/mabar/game/${game.slug}/hasil`, {
                          state: { from: 'community' },
                        })
                      }
                      className="h-8 px-2.5 sm:px-3 rounded-full text-[11px] sm:text-xs font-semibold bg-white border border-[#E0E6ED] hover:bg-gray-50 text-gray-700 flex items-center gap-1 cursor-pointer shrink-0 transition-all"
                    >
                      <Icon icon="ph:list-bullets-bold" className="w-3.5 h-3.5" />
                      <span>Detail</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => navigate(`/host/mabar/game/${game.slug}/rekap`)}
                      className="h-8 px-2.5 sm:px-3 rounded-full text-[11px] sm:text-xs font-semibold bg-white border border-[#E0E6ED] hover:bg-gray-50 text-gray-700 flex items-center gap-1 cursor-pointer shrink-0 transition-all"
                    >
                      <Icon icon="ph:article-bold" className="w-3.5 h-3.5" />
                      <span>Rekap</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => navigate(`/host/mabar/${slug}/game/${game.slug}/edit`)}
                      className="h-8 px-2.5 sm:px-3 rounded-full text-[11px] sm:text-xs font-semibold bg-white border border-[#E0E6ED] hover:bg-gray-50 text-gray-700 flex items-center gap-1 cursor-pointer shrink-0 transition-all"
                    >
                      <Icon icon="ph:pencil-simple-bold" className="w-3.5 h-3.5" />
                      <span>Edit</span>
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setGameToDelete({
                          slug: game.slug,
                          label: `${game.home_team_name || 'Tim Home'} vs ${game.away_team_name || 'Tim Away'}`,
                        })
                      }
                      className="h-8 px-2.5 sm:px-3 rounded-full text-[11px] sm:text-xs font-semibold border border-red-200 bg-white text-red-600 hover:bg-red-50 flex items-center gap-1 cursor-pointer ml-auto shrink-0 transition-all"
                      title="Hapus Game"
                    >
                      <Icon icon="ph:trash-bold" className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Hapus</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : tab === 'standings' ? (
        <StandingCard standings={standing} />
      ) : tab === 'top-score' ? (
        <InvasionPlayerLeaderboard
          type="top-score"
          players={topScorers}
          onShare={handleShareLeaderboardLink}
          onDownload={handleDownloadLeaderboardStatistik}
          downloading={downloadingStatistik}
        />
      ) : tab === 'top-assist' ? (
        <InvasionPlayerLeaderboard
          type="top-assist"
          players={topAssists}
          onShare={handleShareLeaderboardLink}
          onDownload={handleDownloadLeaderboardStatistik}
          downloading={downloadingStatistik}
        />
      ) : tab === 'top-mmr' ? (
        <InvasionPlayerLeaderboard
          type="top-mmr"
          players={topMmrGainers}
          onShare={handleShareLeaderboardLink}
          onDownload={handleDownloadLeaderboardStatistik}
          downloading={downloadingStatistik}
        />
      ) : tab === 'top-save' ? (
        <InvasionPlayerLeaderboard
          type="top-save"
          players={topSavers}
          onShare={handleShareLeaderboardLink}
          onDownload={handleDownloadLeaderboardStatistik}
          downloading={downloadingStatistik}
        />
      ) : null}

      {/* ➕ FLOATING ACTION BUTTON (FAB) FOR MOBILE & DESKTOP */}
      <div className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] right-4 z-40 md:bottom-8 md:right-8">
        <button
          type="button"
          onClick={() => navigate(`/host/mabar/${slug}/game/create`)}
          className="h-12 px-5 rounded-full bg-gradient-to-r from-[#FF8552] to-[#FC480D] text-white text-sm font-bold flex items-center gap-2 shadow-lg hover:shadow-xl transition-all cursor-pointer active:scale-95"
        >
          <Icon icon="ph:plus-bold" className="w-4 h-4 md:w-5 md:h-5" />
          <span>Tambah Game</span>
        </button>
      </div>

      {/* Bottom Sheet Modal Konfirmasi Hapus Game */}
      <Sheet
        open={gameToDelete !== null}
        onOpenChange={(open) => {
          if (!open && !deleting) setGameToDelete(null);
        }}
      >
        <SheetContent
          side="bottom"
          className="rounded-t-3xl border-t border-gray-100 max-h-[90vh] overflow-y-auto px-5 pt-4 pb-8"
        >
          <div className="w-12 h-1 bg-gray-200 rounded-full mx-auto mb-4 shrink-0" />

          <div className="text-center space-y-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto">
              <Icon icon="ph:trash-bold" className="w-6 h-6" />
            </div>
            <SheetHeader className="text-center space-y-1">
              <SheetTitle className="text-lg font-bold text-[#0E1726]">
                Hapus Game?
              </SheetTitle>
              <SheetDescription className="text-xs text-gray-500 max-w-xs mx-auto">
                Game &quot;{gameToDelete?.label}&quot; dan seluruh data lineup akan dihapus permanen. Tindakan ini tidak dapat dibatalkan.
              </SheetDescription>
            </SheetHeader>
          </div>

          <div className="flex flex-col gap-2.5">
            <button
              type="button"
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="w-full h-11 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-md cursor-pointer transition-all active:scale-95 disabled:opacity-50"
            >
              {deleting ? (
                <>
                  <Icon icon="ph:spinner-bold" className="w-4 h-4 animate-spin" />
                  <span>Menghapus...</span>
                </>
              ) : (
                <span>Ya, Hapus Game</span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setGameToDelete(null)}
              disabled={deleting}
              className="w-full h-11 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-xs transition-all cursor-pointer disabled:opacity-50"
            >
              Batal
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <GenerateGameOtomatisModal
        isOpen={isGenerateBulkModalOpen}
        onClose={() => setIsGenerateBulkModalOpen(false)}
        eventSlug={slug!}
        eventSportCategorySlug={event?.sport_category?.slug}
        onSuccess={() => {
          loadData();
          toast.success('Jadwal game berhasil digenerate');
        }}
        onError={(message) => {
          toast.error(message);
        }}
      />
    </div>
  );
};

export default MabarAnalitikPage;
